import { RumError } from '../RumError';

// Максимальная длина селектора для элемента,
// чтобы не превышать лимитов по данным для отправки в RUM
const MAX_SELECTOR_LENGTH = 250;

/**
 * Хелпер для получения селектора элемента, учитывая его родителей.
 * Для построение селектора берется его имя вместе с data-testid.
 * Если data-testid нет, то берется первый класс из classList.
 *
 * @kind helper
 * @param node Элемент, для которого нужно построить селектор.
 * @returns Селектор элемента с учетом его родителей, длиной не превышающей 250 символов.
 **/
export function getSelector(node?: Node | HTMLElement | undefined | null) {
    let selector = '';

    try {
        while (node && node.nodeType !== 9) {
            const nodeName = getNodeName(node);

            let dataTestIdSelector = '';
            if ('dataset' in node) {
                const rawDataTestId = node.dataset.testid?.trim();
                if (rawDataTestId) {
                    const dataTestId = cutDynamicPartOfDataTestId(rawDataTestId);

                    // Модифицируем селектор через ^=,
                    // если обрезанный вариант data-testid не равен длине исходного.
                    dataTestIdSelector =
                        dataTestId.length === rawDataTestId.length
                            ? `[data-testid='${dataTestId}']`
                            : `[data-testid^='${dataTestId}']`;
                }
            }

            let classNameSelector = '';
            if (!dataTestIdSelector && 'classList' in node) {
                const className = node.classList[0]?.trim();
                if (className) {
                    classNameSelector = `.${className}`;
                }
            }

            const selectorPart = dataTestIdSelector
                ? `${nodeName}${dataTestIdSelector}`
                : `${nodeName}${classNameSelector}`;

            const newSelector = selector ? `${selectorPart}>${selector}` : selectorPart;
            if (newSelector.length > MAX_SELECTOR_LENGTH) {
                return selector;
            }

            selector = newSelector;
            node = node.parentNode;
        }
    } catch (error) {
        throw new RumError('Не удалось построить селектор для переданного элемента', { cause: error });
    }

    return selector;
}

/**
 * Хелпер для имени DOM ноды.
 *
 * @kind helper
 * @param node Элемент, для которого нужно получить имя.
 * @returns Имя ноды с учетом того, что нода может не являться элементом (например, текст).
 * Подробнее: https://developer.mozilla.org/ru/docs/Web/API/Node/nodeType
 **/
function getNodeName(node: Node) {
    return node.nodeType === 1 ? node.nodeName.toLowerCase() : node.nodeName.toUpperCase().replace(/^#/, '');
}

/**
 * Хелпер для формирования data-testid для селектора без динамики в конце.
 *
 * @kind helper
 * @param rawDataTestId Изначальный data-testid.
 * @returns Возвращает актуальный data-testid, обрезая всю динамику в конце.
 **/
function cutDynamicPartOfDataTestId(rawDataTestId: string) {
    // Берём часть data-testid до первого невалидного символа, т.к. после идёт динамика:
    // - ID объектов в виде чисел
    // - Названия объектов (кампаний, групп, банеров и т.п.)
    // Дефолт в виде пустой строки из-за типов, а так первый аргумент split всегда строка.
    const [dataTestId = ''] = rawDataTestId.split(/[^a-zA-Z-_.]/);
    // Доудаляем часть динамики в конце, если в data-testid был зашит URL.
    const dataTestIdWithoutUrlEnd = dataTestId.replace(/https?$/, '');

    return dataTestIdWithoutUrlEnd;
}
